import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Map, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Label } from 'components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { MultiSelect } from 'components/ui/multi-select';
import { ConfirmDeleteDialog } from 'components/ConfirmDeleteDialog';
import { useCountries, useSubdivisions } from 'hooks/useReferentials';
import type { Subdivision } from 'api/referentialsApi';
import { subdivisionLabel } from 'utils/subdivisionLabel';
import { cn } from 'lib/utils';
import { controlId, errorId, validationMessage, type FormErrors } from './validation';
import type { TFunction } from 'i18next';

interface ScopeSectionProps {
  countryCode: string;
  geoAreaIds: number[];
  onChange: (patch: { countryCode?: string; geoAreaIds?: number[] }) => void;
  readOnly: boolean;
  /** The scope is blocking on a new record and merely indicative on an old one that predates it. */
  creating?: boolean;
  errors?: FormErrors;
}

/**
 * The remove button says which zone it drops, in the country's own word — "Retirer le
 * département" reads wrong of a province. Local rather than beside `allSubdivisionsLabel`: this
 * screen is its only consumer, and the keys must stay literal for the i18n static guard.
 */
const removeZoneLabel = (t: TFunction, code: string, name: string): string => {
  switch (code) {
    case 'department':
      return t('Remove the department {{name}}', { name });
    case 'province':
      return t('Remove the province {{name}}', { name });
    case 'county':
      return t('Remove the county {{name}}', { name });
    default:
      return t('Remove the area {{name}}', { name });
  }
};

/**
 * The geographic scope, on `user_geo_areas` and never on `users.sectors` — those are Globe Data
 * *commercial* sectors consumed by `/issues/bySectors`, and writing a department code in there
 * breaks two other features.
 */
const ScopeSection = ({ countryCode, geoAreaIds, onChange, readOnly, creating = false, errors = {} }: ScopeSectionProps) => {
  const { t } = useTranslation();
  const { data: countries } = useCountries();
  const { data: subdivisions } = useSubdivisions();
  const [pendingCountry, setPendingCountry] = useState<string | null>(null);

  const country = (countries ?? []).find((entry) => entry.code === countryCode) ?? null;

  // Pickers offer active values only; a deactivated one the record already carries stays in the
  // list so re-saving does not silently drop it.
  const options = useMemo(
    () =>
      (subdivisions ?? [])
        .filter((s) => s.country_code === countryCode && (s.is_active || geoAreaIds.includes(s.id)))
        .map((s) => ({ value: String(s.id), label: `${s.code} — ${s.name}` })),
    [subdivisions, countryCode, geoAreaIds]
  );

  // The rows the design draws, in the order they were picked. An id whose referential row has
  // not arrived (or was purged) is skipped rather than rendered as a blank row.
  const selectedZones = useMemo(
    () =>
      geoAreaIds
        .map((areaId) => (subdivisions ?? []).find((s) => s.id === areaId))
        .filter((zone): zone is Subdivision => zone !== undefined),
    [subdivisions, geoAreaIds]
  );

  const zoneLabel = country ? subdivisionLabel(t, country.subdivision_type, 2) : t('Areas covered');

  // Changing country empties the departments, so it asks first — and cancelling leaves both the
  // country and its selection exactly as they were.
  const confirmCountry = () => {
    if (pendingCountry === null) return;
    onChange({ countryCode: pendingCountry, geoAreaIds: [] });
    setPendingCountry(null);
  };

  /**
   * Radix mirrors the select into a hidden native one as soon as the trigger sits inside a
   * `<form>`, and that mirror fires one change with an empty value while the options are still
   * unmounted. An empty code is never a choice a person made — the picker offers none.
   */
  const pickCountry = (code: string) => {
    if (!code || code === countryCode) return;
    if (geoAreaIds.length === 0) {
      onChange({ countryCode: code, geoAreaIds: [] });
      return;
    }
    setPendingCountry(code);
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <Map className="h-4 w-4" aria-hidden="true" />
            {t('Geographic scope')}
          </CardTitle>
          {/* The design's grey chain pill. Built from the two labels rather than a key of its
              own: the second half is the country's own word and already translated. */}
          <span className="inline-flex h-[26px] shrink-0 items-center whitespace-nowrap rounded-full bg-zinc-100 px-2.5 text-[11.5px] font-medium text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400">
            {t('Country')} → {zoneLabel}
          </span>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor={controlId('countryCode')}>
              {t('Country')}
              {creating && <span className="text-rose-500"> *</span>}
            </Label>
            {/* Always a string, never undefined: switching would make Radix warn about a select
                going from uncontrolled to controlled once the record loads. */}
            <Select value={countryCode} onValueChange={pickCountry} disabled={readOnly}>
              <SelectTrigger
                id={controlId('countryCode')}
                aria-invalid={errors.countryCode ? true : undefined}
                aria-describedby={errors.countryCode ? errorId('countryCode') : undefined}
                className={cn(errors.countryCode && 'border-rose-400 dark:border-rose-500')}
              >
                <SelectValue placeholder={t('Select...')} />
              </SelectTrigger>
              <SelectContent>
                {(countries ?? [])
                  .filter((entry) => entry.is_active || entry.code === countryCode)
                  .map((entry) => (
                    <SelectItem key={entry.code} value={entry.code}>
                      {entry.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            {errors.countryCode && (
              <p id={errorId('countryCode')} className="text-xs text-rose-600 dark:text-rose-400">
                {validationMessage(t, errors.countryCode)}
              </p>
            )}
          </div>

          {/* No picker at all in read mode: the rows below already say the whole truth, and a
              disabled dropdown only invites a click that goes nowhere. */}
          {!readOnly && (
            <div className="space-y-1.5">
              <Label>
                {zoneLabel}
                {creating && <span className="text-rose-500"> *</span>}
              </Label>
              <MultiSelect
                id={controlId('geoAreaIds')}
                aria-label={zoneLabel}
                aria-describedby={errors.geoAreaIds ? errorId('geoAreaIds') : undefined}
                options={options}
                selected={geoAreaIds.map(String)}
                onChange={(values) => onChange({ geoAreaIds: values.map(Number) })}
                disabled={!countryCode}
                searchable
                joinedLabels
                placeholder={
                  countryCode && options.length === 0 ? t('No area for this country yet') : t('Select...')
                }
              />
            </div>
          )}
        </div>

        {/* One bordered row per zone, the shape the design draws: mono code chip, name, country
            on the right, and the remove button only where something can be removed. */}
        {selectedZones.length === 0 ? (
          /* Same box, two voices: the refusal on a creation, the standing hint everywhere else —
             an old record with no scope must still be saveable once another field is fixed. */
          <p
            id={errorId('geoAreaIds')}
            className={cn(
              'rounded-[10px] border border-dashed px-3.5 py-3 text-xs',
              errors.geoAreaIds
                ? 'border-rose-300 text-rose-600 dark:border-rose-800 dark:text-rose-400'
                : 'border-zinc-300 text-zinc-500 dark:border-zinc-700 dark:text-zinc-400'
            )}
          >
            {errors.geoAreaIds
              ? validationMessage(t, errors.geoAreaIds)
              : readOnly
                ? t('No area is attached to this scope yet.')
                : t('Add at least one area: the technician scope follows from it.')}
          </p>
        ) : (
          <ul className="space-y-2">
            {selectedZones.map((zone) => (
              <li
                key={zone.id}
                className="flex flex-wrap items-center gap-3 rounded-[10px] border border-zinc-200 bg-zinc-50 py-[11px] pl-[14px] pr-3 dark:border-zinc-800 dark:bg-zinc-900/40"
              >
                <span className="flex min-w-[190px] flex-1 items-center gap-2.5">
                  <span className="inline-flex h-[23px] shrink-0 items-center rounded-md bg-zinc-100 px-2 font-mono text-xs font-semibold text-zinc-900 dark:bg-zinc-800 dark:text-zinc-50">
                    {zone.code}
                  </span>
                  <span className="text-[13.5px] font-medium text-zinc-900 dark:text-zinc-50">
                    {zone.name}
                  </span>
                </span>
                <span className="flex-1" />
                <span className="whitespace-nowrap text-xs text-zinc-500 dark:text-zinc-400">
                  {zone.country_name}
                </span>
                {!readOnly && (
                  <button
                    type="button"
                    aria-label={removeZoneLabel(t, zone.country_subdivision_type, zone.name)}
                    onClick={() =>
                      onChange({ geoAreaIds: geoAreaIds.filter((areaId) => areaId !== zone.id) })
                    }
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-zinc-500 transition-colors hover:bg-rose-50 hover:text-rose-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900 dark:text-zinc-400 dark:hover:bg-rose-950/50 dark:hover:text-rose-400 dark:focus-visible:ring-zinc-100"
                  >
                    <X className="h-[15px] w-[15px]" aria-hidden="true" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}

        {/* The question the card raises and the design answers: the scope is areas, not shops. */}
        <p className="text-xs leading-[1.4] text-zinc-500 dark:text-zinc-400">
          {t('Points of sale are not attached to the technician: they are identified from the areas of this scope, and picked on the intervention itself.')}
        </p>
      </CardContent>

      {pendingCountry !== null && (
        <ConfirmDeleteDialog
          open
          tone="warning"
          title={t('Change the country?')}
          description={t('The areas already selected will be cleared: they belong to the current country.')}
          confirmLabel={t('Change the country')}
          onClose={() => setPendingCountry(null)}
          onConfirm={confirmCountry}
        />
      )}
    </Card>
  );
};

export default ScopeSection;
